import React, { useCallback } from 'react';
import { cancelHomeChatOperation, replaceHomeChatTurn } from './api';
import { normalizeTopicClarificationEnvelope, normalizeV2Turn } from '../FarChat/api';
import type { RawV2Turn } from '../FarChat/api';
import type { Turn, ClarificationEnvelope, TopicClarificationEnvelope } from '../FarChat/types';
import { isClarificationEnvelope, isTopicClarificationEnvelope } from '../FarChat/types';
import { resetInFlightTurnState } from '../chatTurnState';
import { ProcessingStopControl, isExpectedCancellationError } from './HomeChatTurnErrors';
import { makeRequestId } from './homeChatSessionUtils';
import type { HomeChatPanelState } from './useHomeChatPanelState';
import type { HomeChatSessionReconciliation } from './useHomeChatSessionReconciliation';
import type { HomeChatHydration } from './useHomeChatHydration';


type Inputs = Pick<HomeChatPanelState,
  | 'activeSessionId'
  | 'sessions'
  | 'turns'
  | 'inFlight'
  | 'stoppingOperationId'
  | 'stoppingOperationIdRef'
  | 'setStoppingOperationId'
  | 'pendingRequestIdRef'
  | 'setEditingTurnId'
  | 'setEditDraft'
  | 'editingTurnId'
  | 'editDraft'
  | 'interactionRevisionRef'
  | 'setReplacingTurnId'
  | 'setTurns'
  | 'setInFlight'
  | 'setInFlightUserMsg'
  | 'setInFlightClarification'
  | 'setInFlightTopicFocus'
  | 'setPendingRequestIdSynced'
  | 'scrollThreadToLatest'
  | 'branchRevision'
  | 'activeSessionIdRef'
  | 'setPendingTopicClarification'
  | 'setPendingClarification'
  | 'maybePlayClarificationSound'
  | 'setBranchRevision'
> &
Pick<HomeChatSessionReconciliation,
  | 'reconcileExpectedCancellation'
  | 'refreshSessions'
  | 'refreshAfterTurnDispatch'
> &
Pick<HomeChatHydration,
  | 'notify'
  | 'isStaleResolve'
  | 'hydrateDirectSearchThread'
  | 'clearPendingIfOwned'
>;

export function useHomeChatTurnEditing({
  activeSessionId,
  sessions,
  turns,
  inFlight,
  stoppingOperationId,
  stoppingOperationIdRef,
  setStoppingOperationId,
  reconcileExpectedCancellation,
  pendingRequestIdRef,
  refreshSessions,
  notify,
  setEditingTurnId,
  setEditDraft,
  editingTurnId,
  editDraft,
  interactionRevisionRef,
  setReplacingTurnId,
  setTurns,
  setInFlight,
  setInFlightUserMsg,
  setInFlightClarification,
  setInFlightTopicFocus,
  setPendingRequestIdSynced,
  scrollThreadToLatest,
  branchRevision,
  refreshAfterTurnDispatch,
  isStaleResolve,
  activeSessionIdRef,
  setPendingTopicClarification,
  setPendingClarification,
  maybePlayClarificationSound,
  setBranchRevision,
  hydrateDirectSearchThread,
  clearPendingIfOwned,
}: Inputs) {

  const activeSession = activeSessionId
    ? sessions.find((session) => session.session_id === activeSessionId) || null
    : null;
  const activeOrdinaryOperation = activeSession?.activity
    && activeSession.activity.kind === 'home_chat_turn'
    && !activeSession.gpc_binding
    && !activeSession.mr_binding
    && !activeSession.construction_sow_binding
    && !activeSession.services_pws_chat_binding
    ? activeSession.activity
    : null;
  // The server supplies `editable` as the authorization decision, but the
  // panel also refuses to expose an old response if a stale hydration ever
  // carries that flag forward. A stopped turn is retryable only while it is
  // the current/latest transcript turn and no newer operation is active.
  const latestTurn = turns.length > 0 ? turns[turns.length - 1] : null;
  const editableLatestTurnId = (
    !inFlight
    && !activeOrdinaryOperation
    && latestTurn?.editable === true
    && latestTurn.is_refusal === true
    && typeof latestTurn.turn_id === 'string'
  ) ? latestTurn.turn_id : null;

  const cancellationUnavailableReason = !activeSession?.activity
    ? inFlight
      ? 'Checking how to handle your request. Stop will be available if it continues as a chat response.'
      : 'There is no response to stop.'
    : activeSession.gpc_binding
      || activeSession.mr_binding
      || activeSession.construction_sow_binding
      || activeSession.services_pws_chat_binding
      ? 'This is workflow processing and can’t be stopped from Home Chat.'
      : activeSession.activity.kind !== 'home_chat_turn'
        ? 'This work can’t be stopped from Home Chat.'
        : 'This response can’t be stopped right now.';

  const handleStop = useCallback(async () => {
    if (!activeSessionId || !activeOrdinaryOperation || stoppingOperationId) return;
    stoppingOperationIdRef.current = activeOrdinaryOperation.operation_id;
    setStoppingOperationId(activeOrdinaryOperation.operation_id);
    try {
      const receipt = await cancelHomeChatOperation(
        activeSessionId, activeOrdinaryOperation.operation_id,
      );
      if (receipt.state === 'cancelled') {
        await reconcileExpectedCancellation(
          activeOrdinaryOperation.client_request_id || pendingRequestIdRef.current || undefined,
        );
      } else {
        void refreshSessions({ silently: true });
      }
    } catch (error: any) {
      stoppingOperationIdRef.current = null;
      setStoppingOperationId(null);
      notify('error', error?.message || 'Unable to stop this response.');
    }
  }, [activeSessionId, activeOrdinaryOperation, stoppingOperationId, refreshSessions, notify, reconcileExpectedCancellation]);

  const processingStopControl = (
    <ProcessingStopControl
      canStop={Boolean(activeOrdinaryOperation)}
      stopping={Boolean(
        activeOrdinaryOperation
        && stoppingOperationId === activeOrdinaryOperation.operation_id
      )}
      unavailableReason={cancellationUnavailableReason}
      onStop={() => { void handleStop(); }}
    />
  );

  const handleBeginEdit = useCallback((turn: Turn) => {
    if (turn.turn_id !== editableLatestTurnId || !activeSessionId) return;
    setEditingTurnId(turn.turn_id);
    setEditDraft(turn.user_msg);
  }, [activeSessionId, editableLatestTurnId]);

  const handleCancelEdit = useCallback(() => {
    setEditingTurnId(null);
    setEditDraft('');
  }, []);

  const handleSaveAndResendEdit = useCallback(async () => {
    const sessionId = activeSessionId;
    const turnId = editingTurnId;
    const turn = turnId ? turns.find((candidate) => candidate.turn_id === turnId) : undefined;
    const message = editDraft.trim();
    if (!sessionId || !turn || !message || message === turn.user_msg || inFlight) return;
    const requestId = makeRequestId();
    interactionRevisionRef.current += 1;
    // The edit is visible at once in its existing transcript position. The
    // backend remains authoritative: a conflict or real failure restores the
    // original snapshot and rehydrates rather than creating another message.
    setEditingTurnId(null);
    setEditDraft('');
    setReplacingTurnId(turn.turn_id);
    setTurns((previous) => previous.map((candidate) => (
      candidate.turn_id === turn.turn_id
        ? {
          ...candidate,
          user_msg: message,
          summary: '',
          detail: '',
          citations: [],
          is_refusal: false,
          refusal_text: undefined,
        }
        : candidate
    )));
    setInFlight(true);
    setInFlightUserMsg(null);
    setInFlightClarification(null);
    setInFlightTopicFocus(null);
    setPendingRequestIdSynced(requestId);
    scrollThreadToLatest();
    try {
      const replacement = replaceHomeChatTurn(sessionId, turn.turn_id, {
        message,
        client_request_id: requestId,
        expected_branch_revision: branchRevision,
      });
      // Start activity reconciliation immediately; waiting for the response
      // would leave the edited query apparently inert during generation.
      refreshAfterTurnDispatch();
      const rawReplacement = await replacement;
      if (isStaleResolve(requestId) || activeSessionIdRef.current !== sessionId) return;
      const replacementEnvelope = rawReplacement as (
        | Turn
        | ClarificationEnvelope
        | TopicClarificationEnvelope
      );
      // Replacements re-enter the ordinary server-authoritative router.  A
      // valid replacement can therefore need the same clarification as a
      // fresh message; it is not necessarily an answered Turn.  Do not cast
      // the envelope into a blank turn (which discarded the card and left the
      // old stopped/cancelled branch visible).
      if (isClarificationEnvelope(replacementEnvelope)) {
        setTurns((previous) => previous.filter((candidate) => (
          candidate.turn_id !== turn.turn_id
        )));
        setPendingTopicClarification(null);
        setPendingClarification({ ...replacementEnvelope, session_id: sessionId });
        maybePlayClarificationSound(replacementEnvelope, sessionId, requestId);
      } else if (isTopicClarificationEnvelope(replacementEnvelope)) {
        setTurns((previous) => previous.filter((candidate) => (
          candidate.turn_id !== turn.turn_id
        )));
        setPendingClarification(null);
        setPendingTopicClarification(
          normalizeTopicClarificationEnvelope(
            replacementEnvelope as Partial<TopicClarificationEnvelope> & Record<string, unknown>,
          ),
        );
        maybePlayClarificationSound(replacementEnvelope, sessionId, requestId);
      } else {
        const replacementTurn = normalizeV2Turn(rawReplacement as RawV2Turn);
        setTurns((previous) => previous.map((candidate) => (
          candidate.turn_id === turn.turn_id ? replacementTurn : candidate
        )));
        setPendingClarification(null);
        setPendingTopicClarification(null);
      }
      setBranchRevision((revision) => revision + 1);
      // Rehydrate in the background to receive the server's authoritative
      // branch pruning and any persisted cards without delaying the visible
      // answer that the replace endpoint already returned.
      void hydrateDirectSearchThread(
        sessionId,
        () => activeSessionIdRef.current === sessionId,
      );
      void refreshSessions({ silently: true });
    } catch (error: any) {
      if (isStaleResolve(requestId)) return;
      if (isExpectedCancellationError(error)) {
        await reconcileExpectedCancellation(requestId);
        return;
      }
      if (activeSessionIdRef.current === sessionId) {
        setTurns((previous) => previous.map((candidate) => (
          candidate.turn_id === turn.turn_id ? turn : candidate
        )));
        void hydrateDirectSearchThread(
          sessionId,
          () => activeSessionIdRef.current === sessionId,
        );
      }
      notify('error', error?.message || 'Unable to save and resend that message.');
    } finally {
      if (!isStaleResolve(requestId)) {
        setReplacingTurnId(null);
        resetInFlightTurnState({
          setInFlight,
          clearPendingRequestId: () => clearPendingIfOwned(requestId),
          setInFlightUserMsg,
          setInFlightClarification,
          setInFlightTopicFocus,
        });
      }
    }
  }, [activeSessionId, editingTurnId, turns, editDraft, inFlight, branchRevision, maybePlayClarificationSound, notify, setPendingRequestIdSynced, scrollThreadToLatest, refreshAfterTurnDispatch, isStaleResolve, hydrateDirectSearchThread, refreshSessions, reconcileExpectedCancellation, clearPendingIfOwned]);

  return {
    activeSession,
    editableLatestTurnId,
    processingStopControl,
    handleBeginEdit,
    handleCancelEdit,
    handleSaveAndResendEdit,
  };
}

export type HomeChatTurnEditing = ReturnType<typeof useHomeChatTurnEditing>;
